import { Turbo } from "@hotwired/turbo-rails";
import { getCookie } from "typescript-cookie";

type Fetch = (typeof window)["fetch"];
type FetchParams = Parameters<Fetch>;
type FetchRequestHeaders = Record<string, string>;
type FetchOptions = Omit<NonNullable<FetchParams[1]>, "headers"> & {
  headers?: FetchRequestHeaders;
};

function metaContent(name: string) {
  return document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content;
}

export function getCSRFToken() {
  const name = metaContent("csrf-param");
  if (name) {
    return getCookie(name) || metaContent("csrf-token");
  }
}

// The hidden field a plain HTML form needs, name and value from the same meta tags — so the two
// cannot disagree the way a hardcoded `authenticity_token` beside a meta-derived value can.
export function csrfField(): { name: string; value: string } {
  return { name: metaContent("csrf-param") ?? "authenticity_token", value: getCSRFToken() ?? "" };
}

export class FetchError extends Error {
  constructor(
    message: string,
    public status: number,
    public response: Response,
    public json?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "FetchError";
  }
}

export function fetchErrorMessage(err: unknown, fallback: string): string {
  return err instanceof FetchError && typeof err.json?.["error"] === "string"
    ? err.json["error"]
    : fallback;
}

/**
 * This browser tab, on every request it makes (Turbo's too, see tab_state_controller), so a
 * broadcast about a change can say which tab made it: that tab has refetched already, and only the
 * other tabs showing the same patient need to.
 */
export const TAB_ID = crypto.randomUUID();

export async function coreFetch(
  resource: FetchParams[0],
  options: FetchOptions,
): ReturnType<Fetch> {
  const headers: FetchRequestHeaders = {
    "X-Requested-With": "XMLHttpRequest",
    "X-Tab-Id": TAB_ID,
    ...options.headers,
  };
  const token = options.method && options.method !== "GET" && getCSRFToken();
  if (token) {
    headers["X-CSRF-Token"] = token;
  }
  const response = await fetch(resource, { ...options, headers });
  if (response.ok) {
    return response;
  }
  throw new FetchError(`Failed response ${response.status}`, response.status, response);
}

/**
 * coreFetch rejects before reading the body, so a server's `{ error }` never reaches
 * fetchErrorMessage. This re-throws a failed request with its JSON body attached. For a request
 * whose success has no body (a 204), where fetchJSON would fail parsing it.
 */
export async function withJSONErrorBody<R>(request: Promise<R>): Promise<R> {
  try {
    return await request;
  } catch (error) {
    if (error instanceof FetchError) {
      let json: Record<string, unknown> | undefined;
      try {
        json = (await error.response.json()) as Record<string, unknown>;
      } catch {
        // ignore
      }

      if (json) {
        throw new FetchError(error.message, error.status, error.response, json);
      }
    }

    throw error;
  }
}

export async function fetchJSON<R>(resource: FetchParams[0], options?: FetchOptions): Promise<R> {
  const headers: FetchRequestHeaders = {
    Accept: "application/json",
    ...options?.headers,
  };

  const response = await withJSONErrorBody(coreFetch(resource, { ...options, headers }));
  return await (response.json() as Promise<R>);
}

const inFlightJSON = new Map<string, Promise<unknown>>();

/**
 * fetchJSON, but a second call with the same key joins the request already in flight instead of
 * issuing another. Aborting a request client-side does not stop the server finishing it, so a
 * component that unmounts and remounts mid-request would otherwise queue a second copy of work
 * the first one is still doing. The entry clears when the request settles, so a later call
 * fetches fresh.
 */
export function fetchJSONOnce<R>(
  key: string,
  resource: FetchParams[0],
  options?: FetchOptions,
): Promise<R> {
  const existing = inFlightJSON.get(key);
  if (existing) return existing as Promise<R>;

  const request = fetchJSON<R>(resource, options).finally(() => {
    if (inFlightJSON.get(key) === request) inFlightJSON.delete(key);
  });
  inFlightJSON.set(key, request);
  return request;
}

export async function fetchAndProcessTurboStream(
  resource: FetchParams[0],
  options?: FetchOptions,
): Promise<void> {
  const process = await fetchAndDeferredProcessTurboStream(resource, options);
  process();
}

export async function fetchAndDeferredProcessTurboStream(
  resource: FetchParams[0],
  options?: FetchOptions,
): Promise<() => void> {
  const headers: FetchRequestHeaders = {
    Accept: "text/vnd.turbo-stream.html",
    ...options?.headers,
  };
  const response = await coreFetch(resource, { ...options, headers });
  const text = await response.text();

  return () => {
    Turbo.renderStreamMessage(text);
  };
}
